// Notification click → destination router.
//
// The App Router treats `router.push('/foo#bar')` as a no-op when the
// current pathname is already `/foo` with a DIFFERENT hash — it updates
// the URL bar but does NOT fire `hashchange` or re-run mount effects. It
// can also leave the URL in a buggy state like `/foo#old#new` when the
// current URL already has a hash (that's what caused the manual-deposit
// notification click on /admin/payment#withdrawal-history to render a
// broken `#withdrawal-history#manual-deposit-history` URL and open the
// wrong tab).
//
// Pages that swap tabs off `#anchor` listeners (e.g. /admin/payment
// listening for #recent-topups / #withdrawal-history /
// #manual-deposit-history) rely on `hashchange` to trigger — so ANY
// notification click that lands on a same-pathname target with only a
// hash change must set `window.location.hash` directly (which does
// replace the fragment correctly AND fires `hashchange`). Cross-page
// navigations still go through the App Router so prefetch + client
// transitions still work.
//
// Both entry points that route notification clicks (the floating toast
// in notification-provider.tsx and the notifications list at
// /admin/my-notifications) should call this helper so their behaviour
// stays identical.
export function navigateFromNotificationPath(
  path: string,
  routerPush: (href: string) => void,
): void {
  if (typeof window === 'undefined') {
    routerPush(path);
    return;
  }
  let target: URL;
  try {
    target = new URL(path, window.location.origin);
  } catch {
    routerPush(path);
    return;
  }
  const samePath = target.pathname === window.location.pathname;
  if (!samePath) {
    routerPush(path);
    return;
  }
  // Same pathname.
  if (!target.hash) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  if (target.hash === window.location.hash) {
    // Already at the exact URL. Fire hashchange so the target page's
    // listener re-runs (tab switch + scroll to anchor). Use a plain Event
    // — HashChangeEvent constructors are spotty in older Safari.
    window.dispatchEvent(new Event('hashchange'));
    return;
  }
  // Different hash — assigning REPLACES the fragment and fires hashchange.
  window.location.hash = target.hash;
}
